import { Clipboard } from '@angular/cdk/clipboard';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { Component, computed, inject, signal } from '@angular/core';
import { MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { Router } from '@angular/router';
import { diegoRoutes } from '@app/core/enums/orderflow-paths';
import { Dealer } from '@app/core/models/dealer.model';
import { UpdateActiveDealerAction } from '@app/core/states/dealer/dealer.actions';
import { DealerStateSelectors } from '@app/core/states/dealer/dealer-state.selectors';
import { UserSelectors } from '@app/core/states/user/user.selectors';
import { IconNames } from '@app/shared/enums/icon-names';
import { CustomerSelectors } from '@app/sales/modules/details/states/customer.selectors';
import { OrderSelectors } from '@app/sales/states/order/order.selectors';
import { select, Store } from '@ngxs/store';

interface CommandPaletteCommand {
  id: string;
  label: string;
  icon: IconNames;
  action: () => void;
  keepOpen?: boolean;
}

@Component({
  selector: 'app-command-palette',
  templateUrl: './command-palette.component.html',
  styleUrl: './command-palette.component.scss',
  imports: [CdkScrollable, MatDialogContent, MatIcon],
})
export class CommandPaletteComponent {
  private dialogRef = inject<MatDialogRef<CommandPaletteComponent>>(MatDialogRef);
  private clipboard = inject(Clipboard);
  private store = inject(Store);
  private router = inject(Router);

  private activeDealer = select(DealerStateSelectors.activeDealer);
  private dealers = select(DealerStateSelectors.dealers);
  private userId = select(UserSelectors.userId);
  private customerId = select(CustomerSelectors.customerId);
  private orderId = select(OrderSelectors.currentOrderServiceId);

  IconNames = IconNames;

  filterText = signal('');
  activeIndex = signal(0);
  showChangeDealer = signal(false);

  private commands = computed<CommandPaletteCommand[]>(() => {
    const commands: CommandPaletteCommand[] = [
      {
        id: 'copy-dealer-id',
        label: 'Kopier dealer-id (' + (this.activeDealer()?.dealerName ?? '') + ')',
        icon: IconNames.ice_copy,
        action: () => this.clipboard.copy(this.activeDealer()?.dealerId ?? ''),
      },
      {
        id: 'copy-user-id',
        label: 'Kopier user-id',
        icon: IconNames.ice_copy,
        action: () => this.clipboard.copy(this.userId() ?? ''),
      },
      {
        id: 'change-dealer',
        label: 'Bytt dealer',
        icon: IconNames.ice_shop,
        action: () => this.openChangeDealer(),
        keepOpen: true,
      },
    ];

    if (this.customerId()) {
      commands.push({
        id: 'copy-customer-id',
        label: 'Kopier customer-id',
        icon: IconNames.ice_copy,
        action: () => this.clipboard.copy(this.customerId() ?? ''),
      });
    }

    if (this.orderId()) {
      commands.push({
        id: 'copy-order-id',
        label: 'Kopier order-id',
        icon: IconNames.ice_copy,
        action: () => this.clipboard.copy(this.orderId() ?? ''),
      });
    }

    return commands;
  });

  filteredCommands = computed(() => {
    const query = this.filterText().trim().toLowerCase();
    const commands = this.commands();
    if (!query) {
      return commands;
    }
    return commands.filter(command => command.label.toLowerCase().includes(query));
  });

  filteredDealers = computed(() => {
    const query = this.filterText().trim().toLowerCase();
    const dealers = this.dealers() ?? [];
    const matches = !query
      ? dealers
      : dealers.filter(
          dealer => dealer.displayName?.toLowerCase().includes(query) || dealer.dealerId?.toLowerCase().includes(query)
        );

    const activeDealerId = this.activeDealer()?.dealerId;
    return [...matches].sort((a, b) => {
      if (a.dealerId === activeDealerId) {
        return -1;
      }
      if (b.dealerId === activeDealerId) {
        return 1;
      }
      return 0;
    });
  });

  onFilterChange(value: string): void {
    this.filterText.set(value);
    this.activeIndex.set(0);
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.showChangeDealer()) {
      event.preventDefault();
      event.stopPropagation();
      this.backToCommands();
      return;
    }

    const items: (CommandPaletteCommand | Dealer)[] = this.showChangeDealer()
      ? this.filteredDealers()
      : this.filteredCommands();

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.activeIndex.set(items.length ? (this.activeIndex() + 1) % items.length : 0);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.activeIndex.set(items.length ? (this.activeIndex() - 1 + items.length) % items.length : 0);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const item = items[this.activeIndex()];
      if (!item) {
        return;
      }
      if (this.showChangeDealer()) {
        this.selectDealer(item as Dealer);
      } else {
        this.runCommand(item as CommandPaletteCommand);
      }
    }
  }

  runCommand(command: CommandPaletteCommand): void {
    command.action();
    if (!command.keepOpen) {
      this.dialogRef.close();
    }
  }

  isActiveDealer(dealer: Dealer): boolean {
    return dealer.dealerId === this.activeDealer()?.dealerId;
  }

  selectDealer(dealer: Dealer): void {
    this.store.dispatch(new UpdateActiveDealerAction(dealer));
    this.router.navigate([diegoRoutes.startSale]);
    this.dialogRef.close();
  }

  backToCommands(): void {
    this.showChangeDealer.set(false);
    this.filterText.set('');
    this.activeIndex.set(0);
  }

  private openChangeDealer(): void {
    this.showChangeDealer.set(true);
    this.filterText.set('');
    this.activeIndex.set(0);
  }
}
